import { createContext, useContext } from "react";
import type { ActingNode } from "@pirouz-ca/ui-components";
import type { OrderingSurface } from "../shell/OrderingShell.js";
import type { ContextNodeType } from "./contextNodes.js";

/**
 * What everything below `OrderingContextProvider` may rely on (ORDR-41).
 *
 * Only ever a RESOLVED answer: the provider renders its own loading, sign-in
 * and error states and mounts children only once it has one. So `nodes: []`
 * here always means the server said "none", never "we could not ask".
 */
export interface OrderingContextValue {
  readonly surface: OrderingSurface;
  /** The Site from the address on the customer surface; `null` otherwise. */
  readonly siteId: string | null;
  /**
   * The brand a brand domain resolved to (ORDR-164): the host door's answer
   * from `flow/?entry=BRAND_DOMAIN&host=`. `null` on every other door, where a
   * brand, when there is one, is the address's own `?brand=`.
   */
  readonly brandNodeId: string | null;
  /** The caller's acting nodes on the staff surface; always `[]` for a customer. */
  readonly nodes: readonly ActingNode[];
  /**
   * The Site each staff node trades at, from the context's `site_id` (ORDR-42).
   * A node with no current Site is absent. Always `{}` for a customer.
   */
  readonly siteIdByNode: Readonly<Record<string, string>>;
  /**
   * Each staff node's `node_type` (ORDR-194). The shell offers a RESELLER node
   * only "Onboard restaurant". Always `{}` for a customer.
   */
  readonly nodeTypeByNode: Readonly<Record<string, ContextNodeType>>;
  /**
   * The signed-in user on the staff surface; `null` for a customer (ORDR-94).
   *
   * The grants panel needs it to answer "what may I confer", which is decided
   * by the caller's OWN grants. It is an identifier for filtering, never an
   * authorization input: the server re-derives the caller from the session
   * cookie on every write and ignores anything the client says about identity.
   */
  readonly ownUserId: string | null;
  /**
   * The business the session acts for, from `/auth/me/memberships` (ORDR-204):
   * `null` when none is active, while it is being read, or if it could not be
   * read, and always `null` for a customer. Onboarding waits for one: without it
   * paio-onboarding answers 403 "no acting firm".
   */
  readonly activeFirmId: string | null;
}

export const OrderingContext = createContext<OrderingContextValue | null>(null);

/** Refuses outside the provider rather than inventing an empty context. */
export function useOrderingContext(): OrderingContextValue {
  const value = useContext(OrderingContext);
  if (value === null) {
    throw new Error("useOrderingContext must be used inside <OrderingContextProvider>");
  }
  return value;
}
